<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { checkmarkOutline, chevronForwardOutline, chevronDownOutline, ticketOutline, closeOutline } from 'ionicons/icons';
import CheckboxInput from '@/shared/components/sign-up-forms/CheckboxInput.vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { Cart } from '@/shared/interfaces/cart';
import { useCart } from '@/modules/shop/composables/useCart';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import TextInput from '@/shared/components/sign-up-forms/TextInput.vue';
import { EMAIL_REGX } from '@/shared/constants/email';
import useTenant from '@/shared/composables/useTenant';
import { useAuthStore } from '@/stores/auth/authStore';

const props = defineProps({
	shouldBlockChanges: { type: Boolean, default: false },
	cartItem: { type: Object as () => Cart, required: true },
	cartItemProductId: { type: Number, required: true },
	cartItemIndex: { type: Number, required: true },
	eventId: { type: Number, required: true },
});

const { t } = useI18n();
const isOpen = ref(false);
const { updateCartAdditionalInfoByIndex } = useCart();
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig } = getTenantConfigAndStyles();
const ticketHolderName = ref('');
const ticketHolderEmail = ref('');
const isUserTicketHolder = ref(false);
const { isAuthenticated } = useAuthStore();

// **Validation Schema**
const validationSchema = yup.object({
	memberEmail: yup.string().required().label(t('events.tickets.add_members.email_label')).matches(EMAIL_REGX, t('events.tickets.add_members.errors.email')),
	memberFullName: yup.string().required().label(t('events.tickets.add_members.full_name_label')),
});

// **Initialize Form**
const { meta, validate, setFieldValue, values, resetForm } = useForm({
	validationSchema,
	initialValues: {
		memberFullName: '',
		memberEmail: '',
	},
});

onMounted(async () => {
	if (props.cartItemIndex === 0 && tenantConfig && tenantConfig.value && !tenantConfig.value.enable_full_guest_mode && isAuthenticated()) {
		await toggleAssignToMe();
	} else if (props.cartItem.additionalInfo?.name) {
		ticketHolderName.value = props.cartItem.additionalInfo.name;
		ticketHolderEmail.value = props.cartItem.additionalInfo.email;
	}
});

const toggleAssignToMe = async () => {
	ticketHolderName.value = props.cartItem.additionalInfo?.name || userProfile.value?.full_name || '';
	ticketHolderEmail.value = props.cartItem.additionalInfo?.email || userProfile.value?.email || '';
	isUserTicketHolder.value = !isUserTicketHolder.value;

	if (isUserTicketHolder.value) {
		// Wait for Vue updates before setting values
		setTimeout(() => {
			setFieldValue('memberFullName', ticketHolderName.value);
			setFieldValue('memberEmail', ticketHolderEmail.value);
		}, 1000);
	} else {
		setFieldValue('memberFullName', '');
		setFieldValue('memberEmail', '');
		ticketHolderName.value = '';
		ticketHolderEmail.value = '';
	}

	// **Ensure auto-save on first render if fields were empty**
	if (!props.cartItem.additionalInfo?.name) {
		updateCartAdditionalInfoByIndex(props.cartItemIndex, {
			name: ticketHolderName.value,
			email: ticketHolderEmail.value,
		});
	}
};

// **Handle Form Submission**
const onSubmit = async (event: Event) => {
	event.preventDefault();
	const isValid = await validate();
	if (isValid.valid) {
		ticketHolderName.value = values.memberFullName;
		ticketHolderEmail.value = values.memberEmail;
		await updateCartAdditionalInfoByIndex(props.cartItemIndex, {
			name: values.memberFullName,
			email: values.memberEmail,
		});
	}
};

// **Prevent Accordion Toggle When Clicking Checkbox**
const toggleAccordion = (event: Event) => {
	if (!(event.target as HTMLElement).closest('.checkbox-container')) {
		isOpen.value = !isOpen.value;
	}
};

// Remove member
const removeMember = () => {
	isUserTicketHolder.value = false;
	ticketHolderName.value = '';
	ticketHolderEmail.value = '';

	updateCartAdditionalInfoByIndex(props.cartItemIndex, {
		...props.cartItem.additionalInfo,
		name: null,
		email: null,
		user_id: null,
	});
	resetForm();
};
</script>

<template>
	<ion-accordion-group>
		<ion-accordion class="mb-4 items-start justify-between backdrop-blur-3xl bg-[#3f3e4a] rounded-lg">
			<div class="flex w-full justify-between h-[70px] px-4 py-2 cursor-pointer" slot="header" @click="toggleAccordion">
				<div class="flex items-center">
					<ion-icon :class="!ticketHolderName ? 'text-white' : 'text-anykrowd-ticket-check-green'" :icon="!ticketHolderName ? ticketOutline : checkmarkOutline" class="w-[22px] h-[22px] py-2 rounded-full"></ion-icon>
					<div class="flex flex-col ml-2 mr-2">
						<span class="font-sourcesanspro-semibold text-sm text-white/50 leading-none">{{ cartItem?.product.name }} :</span>
						<div class="flex items-center">
							<span class="font-sourcesanspro-semibolditalic text-m2 leading-none">
								{{ ticketHolderName || t('components.events_item_member_adder.empty') }}
							</span>
						</div>
					</div>
				</div>
				<div class="flex items-center checkbox-container">
					<ion-icon :icon="isOpen ? chevronDownOutline : chevronForwardOutline" class="text-white"></ion-icon>
				</div>
			</div>

			<div :class="[{ 'flex': props.cartItem.additionalInfo.name }]" class="ion-padding border-t border-t-white/10 font-sourcesanspro-regular text-base2 font-color-text-50" slot="content">
				<div v-show="!props.cartItem.additionalInfo.name || !props.cartItem.additionalInfo.email" class="w-full">
					<div class="flex items-center" v-if="tenantConfig && !tenantConfig.enable_full_guest_mode && isAuthenticated()">
						<CheckboxInput id="userTicketHolder" name="userTicketHolder" container-class="w-[20px]" type="checkbox" :defaultValue="isUserTicketHolder" @click="toggleAssignToMe">
							<label for="userTicketHolder" class="font-sourcesanspro-semibolditalic text-white text-sm">{{ t('components.events_item_member_adder.assign_to_me') }}</label>
						</CheckboxInput>
					</div>
					<form @submit="onSubmit" class="w-full">
						<div class="relative mb-4">
							<TextInput wrapper-class="flex flex-wrap justify-left item-center mb-2 border border-solid focus-within:background-white-15" v-model="ticketHolderName" name="memberFullName" type="text" :placeholder="t('components.events_item_member_adder.memberFullName')" />
						</div>
						<TextInput wrapper-class="flex flex-wrap justify-left item-center mb-2 border border-solid focus-within:background-white-15" v-model="ticketHolderEmail" name="memberEmail" type="email" :placeholder="t('components.events_item_member_adder.memberEmail')" />
						<ion-button type="submit" :disabled="!meta.valid" shape="round" class="hover:rounded-full hover:bg-white">
							{{ t('components.events_item_member_adder.save') }}
						</ion-button>
					</form>
				</div>
				<template v-if="props.cartItem.additionalInfo.name && props.cartItem.additionalInfo.email">
					<div class="flex grow items-center justify-between h-[55px] mr-2 px-4 py-2 rounded-full border border-anykrowd-gray/20 background-anykrowd-gray mb-4">
						<div class="flex">
							<ion-icon class="cursor-pointer mr-2" :icon="ticketOutline" slot="end"></ion-icon>
							<span class="font-sourcesanspro-semibold text-base2 text-white">{{ props.cartItem.additionalInfo.name }}</span>
						</div>
						<ion-button class="transparent ion-no-margin ion-no-padding" shape="round" @click="removeMember">
							<ion-icon class="cursor-pointer" :icon="closeOutline"></ion-icon>
						</ion-button>
					</div>
					<!-- <ion-button
						v-if="!shouldBlockChanges"
						class="rounded ion-no-padding ion-no-margin transparent"
						:routerLink="`/friends/select?type=ticket-creation&id=${props.cartItemIndex}&eventId=${props.eventId}`">
						<ion-icon
							:icon="require('@/statics/svg/people-add.svg')"
							class="w-[50px] h-[50px] cursor-pointer"></ion-icon>
					</ion-button> -->
				</template>
			</div>
		</ion-accordion>
	</ion-accordion-group>
</template>

<style scoped>
input {
	background-color: #26252f;
	border: 1px solid rgba(255, 255, 255, 0.3);
	color: white;
	padding: 10px;
	border-radius: 6px;
	outline: none;
}
input:focus {
	border-color: white;
	box-shadow: 0 0 5px white;
}
</style>
